import * as React from 'react'
import { RefreshCw, Search, Settings2 } from 'lucide-react'
import { Link } from 'react-router-dom'
import { useListNotificationsQuery } from '@axilon-apps/domains-ontology-ui-state'
import { Button } from '../../components/ui/button'
import { Card } from '../../components/ui/card'
import { Input } from '../../components/ui/input'
import { Tabs as TabsRoot, TabsList, TabsTrigger } from '../../components/ui/tabs'
import { Section } from '../../components/primitives/Section'
import { Empty } from '../../components/primitives/Empty'
import { cn } from '../../lib/utils'
import { NotificationItem } from '../../components/Notifications/NotificationItem'
import {
  type PresentedNotification,
  presentNotifications
} from '../../components/Notifications/notification-presentation'
import { useNotificationActions } from '../../components/Notifications/useNotificationActions'

type PageTab = 'needs' | 'activity' | 'done'

const EMPTY_COPY: Record<PageTab, { title: string; hint?: string }> = {
  needs: {
    title: 'Nothing needs you',
    hint: 'Findings, failures and requests that need a decision show up here.'
  },
  activity: { title: 'No recent activity' },
  done: { title: 'Nothing handled yet' }
}

function matches(item: PresentedNotification, q: string): boolean {
  const p = item.presentation
  return [p.title, p.context, p.kindLabel, p.source, item.notification.summary]
    .filter(Boolean)
    .some((field) => String(field).toLowerCase().includes(q))
}

const NotificationsPage = () => {
  const [tab, setTab] = React.useState<PageTab>('needs')
  const [query, setQuery] = React.useState('')
  const [forYou, setForYou] = React.useState(false)
  const scope = forYou ? { for_you: true } : {}
  const openList = useListNotificationsQuery({ state: 'open', page_size: 200, ...scope })
  const doneList = useListNotificationsQuery({ state: 'done', page_size: 200, ...scope })
  const { isLoading, error } = tab === 'done' ? doneList : openList
  const isFetching = openList.isFetching || doneList.isFetching
  const refetch = () => {
    void openList.refetch()
    void doneList.refetch()
  }
  const { open, run } = useNotificationActions()

  const buckets = React.useMemo(() => {
    const openItems = presentNotifications(openList.data ?? [])
    return {
      needs: openItems.filter((i) => i.presentation.bucket === 'action'),
      activity: openItems.filter((i) => i.presentation.bucket === 'activity'),
      done: presentNotifications(doneList.data ?? [])
    }
  }, [openList.data, doneList.data])

  const visible = React.useMemo(() => {
    const q = query.trim().toLowerCase()
    return q ? buckets[tab].filter((i) => matches(i, q)) : buckets[tab]
  }, [buckets, tab, query])

  const trigger = (value: PageTab, label: string) => (
    <TabsTrigger value={value} className="gap-1.5">
      {label}
      <span className="font-mono text-11 text-ink-3">{buckets[value].length}</span>
    </TabsTrigger>
  )

  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-card">
      <header>
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="m-0 text-2xl font-semibold text-ink">Notifications</h1>
            <p className="mt-1 text-sm text-ink-3">
              What needs a decision from you, and what happened recently.
            </p>
          </div>
          <Button asChild variant="ghost" size="sm">
            <Link to="/settings?tab=notifications">
              <Settings2 />
              Settings
            </Link>
          </Button>
        </div>
      </header>
      <Section
        label="Inbox"
        action={
          <TabsRoot value={tab} onValueChange={(v) => setTab(v as PageTab)}>
            <TabsList variant="line">
              {trigger('needs', 'Needs you')}
              {trigger('activity', 'Activity')}
              {trigger('done', 'Done')}
            </TabsList>
          </TabsRoot>
        }
      >
        <div className="mb-3 flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-ink-3" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search all notifications…"
              className="pl-8"
            />
          </div>
          <Button
            variant={forYou ? 'secondary' : 'outline'}
            size="sm"
            aria-pressed={forYou}
            onClick={() => setForYou((v) => !v)}
          >
            For you
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            disabled={isFetching}
            aria-label="Refresh notifications"
          >
            <RefreshCw className={cn(isFetching && 'animate-spin')} />
            Refresh
          </Button>
        </div>
        <Card className="gap-0 overflow-hidden py-0">
          {error ? (
            <div className="px-4 pt-4">
              <div role="alert" className="rounded-md bg-st-down-bg px-3 py-2 text-sm text-st-down">
                Failed to load notifications.
              </div>
            </div>
          ) : null}
          {isLoading && visible.length === 0 ? (
            <div className="px-4 py-6 text-center text-sm text-ink-3">Loading notifications…</div>
          ) : visible.length === 0 ? (
            <Empty
              title={query ? 'No notifications match your search' : EMPTY_COPY[tab].title}
              hint={query ? 'Try a different keyword.' : EMPTY_COPY[tab].hint}
            />
          ) : (
            <ul className="divide-y divide-border">
              {visible.map((item) => (
                <li key={item.presentation.groupKey}>
                  <NotificationItem
                    item={item}
                    density="page"
                    readOnly={tab === 'done'}
                    onOpen={open}
                    onAction={run}
                  />
                </li>
              ))}
            </ul>
          )}
        </Card>
      </Section>
    </div>
  )
}

export default NotificationsPage
